import { useEffect, useState } from "react";
import { ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { addRequest, uid, updateRequest } from "../../lib/storage";

// Converts a shortfall into a brand-new purchase request (the "direct
// purchase" fallback) and stamps the originating request line with a
// back-reference so it's no longer listed in the pending panel.
export default function CreateDirectPurchaseDialog({
  open,
  onOpenChange,
  shortfall, // { key, request, line, product, missing } | null
  suppliers,
  user,
  onCreated,
}) {
  const [vendor, setVendor] = useState("");
  const [qty, setQty] = useState(0);
  const [note, setNote] = useState("Direct purchase — covering missed delivery");

  useEffect(() => {
    if (!open || !shortfall) return;
    setVendor(suppliers.find((s) => s.type === "direct")?.id || suppliers[0]?.id || "");
    setQty(shortfall.missing || 1);
    setNote("Direct purchase — covering missed delivery");
  }, [open, shortfall, suppliers]);

  const submit = () => {
    if (!shortfall) return;
    if (!vendor) return toast.error("Pick a vendor");
    const q = Math.max(1, Number(qty) || 0);
    const nowIso = new Date().toISOString();
    const newId = uid("r");
    const product = shortfall.product;
    const line = {
      productId: shortfall.line.productId,
      productName: product?.name || shortfall.line.productName,
      unit: product?.unit || shortfall.line.unit || "ea",
      quantity: q,
      note,
    };
    addRequest({
      id: newId,
      type: "purchase",
      restaurant: shortfall.request.restaurant,
      fromRestaurant: null,
      toRestaurant: null,
      vendor,
      supplierType: "external",
      requestedBy: user.id,
      requestedByName: user.name,
      requesterRole: user.role,
      requesterArea: shortfall.request.requesterArea || null,
      requesterAreas: shortfall.request.requesterAreas || [],
      items: [line],
      status: "submitted",
      notes: note,
      approvedBy: null,
      approvedAt: null,
      createdAt: nowIso,
      updatedAt: nowIso,
      directPurchaseFor: {
        originRequestId: shortfall.request.id,
        originProductId: shortfall.line.productId,
      },
    });
    // Back-reference on origin line so it leaves the pending list.
    const originItems = (shortfall.request.items || []).map((it) =>
      it.productId === shortfall.line.productId
        ? { ...it, convertedToRequestId: newId }
        : it
    );
    updateRequest(shortfall.request.id, { items: originItems });
    toast.success("Direct purchase request created");
    onCreated?.();
    onOpenChange(false);
  };

  if (!shortfall) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-[#FBF8F1] border-[#E6E0D4]">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#0B1F3A] flex items-center gap-2">
            <ShoppingBag size={20} /> Create direct purchase
          </DialogTitle>
          <DialogDescription className="text-[#0B1F3A]/65">
            {shortfall.product?.name || shortfall.line.productName} — missing{" "}
            <span className="font-medium">{shortfall.missing}</span> {shortfall.line.unit || shortfall.product?.unit}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label className="text-[10.5px] tracking-eyebrow uppercase text-[#0B1F3A]/60">Vendor</Label>
            <Select value={vendor} onValueChange={setVendor}>
              <SelectTrigger data-testid="direct-vendor-trigger" className="mt-1 bg-white border-[#E6E0D4]">
                <SelectValue placeholder="Choose vendor" />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name} · {s.type === "delivered" ? "Delivered" : "Direct"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10.5px] tracking-eyebrow uppercase text-[#0B1F3A]/60">Quantity</Label>
            <Input
              data-testid="direct-qty-input"
              type="number"
              min={1}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="mt-1 bg-white border-[#E6E0D4]"
            />
          </div>
          <div>
            <Label className="text-[10.5px] tracking-eyebrow uppercase text-[#0B1F3A]/60">Note</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="mt-1 bg-white border-[#E6E0D4] min-h-[56px]"
            />
          </div>
        </div>
        <DialogFooter className="pt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            data-testid="direct-create-submit"
            onClick={submit}
            className="bg-[#0B1F3A] hover:bg-[#11294A] text-white"
          >
            Create request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
